import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Button,
    TextField,
    Typography,
    Container,
    Paper,
    InputAdornment,
    CircularProgress,
    useTheme
} from '@mui/material';
import { Email, ArrowBack } from '@mui/icons-material';
import { forgotPassword } from '../../Redux/Reducers/AuthReducer';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const ForgotPassword = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: {
            email: '',
        },
    });

    const onSubmit = async (data) => {
        setLoading(true);
        try {
            const res = await dispatch(
                forgotPassword(data.email)
            ).unwrap();
            toast.success(res.message);
        } catch (err) {
            toast.error(
                typeof err === 'string'
                    ? err
                    : t('failed_send_link', 'Failed to send reset link')
            );
        } finally {
            setLoading(false);
        }
    };



    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 2,
                backgroundImage: 'url(/auth/auth-bg.webp)',
                backgroundSize: 'contain',
                backgroundPosition: 'top right',
                backgroundRepeat: 'no-repeat',
            }}
        >
            <Container maxWidth={false} sx={{ maxWidth: '550px', }}>
                <Paper
                    elevation={24}
                    sx={{
                        background: 'rgba(255, 255, 255, 0)',
                        color: '#fff',
                        boxShadow: 'none',
                        borderRadius: 0,
                    }}
                >
                    <Box sx={{ mb: '20px' }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: 'white', mb: '10px' }}>
                            {t('forgot_password', 'Forgot')}{' '}
                            <Box
                                component="span"
                                sx={{
                                    color: '#FF69B4',
                                }}
                            >
                                {t('forgot_password_title_p2', 'Password')}
                            </Box>
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 1)' }}>
                            {t('forgot_password_subtitle', "Enter your email and we'll send you a link to reset your password")}
                        </Typography>
                    </Box>

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            type="email"
                            {...register('email', {
                                required: t('email_required', 'Email is required'),
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: t('invalid_email', 'Invalid email address')
                                }
                            })}
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            placeholder={t('email_placeholder', 'Enter email')}
                            sx={{
                                mb: '20px',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '999px',
                                    backgroundColor: 'transparent',
                                    color: '#fff',

                                    '& fieldset': {
                                        borderColor: 'rgba(230, 210, 249, 0.2)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgba(230, 210, 249, 0.2)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'rgba(230, 210, 249, 0.2)',
                                        borderWidth: '1px',
                                    },
                                },
                                '& .MuiOutlinedInput-input': {
                                    padding: '12px 20px',
                                    color: '#FF69B4',
                                },
                                '& input::placeholder': {
                                    color: 'rgba(255,255,255,0.6)',
                                    opacity: 1,
                                },
                                '& .MuiFormHelperText-root': {
                                    ml: '20px'
                                }
                            }}
                            InputLabelProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
                        />

                        <Button
                            fullWidth
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                borderRadius: '999px',
                                fontWeight: '400',
                                fontSize: '16px',
                                lineHeight: '24px',
                                textTransform: 'none',
                                background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : t('send_reset_link', 'Send Reset Link')}
                        </Button>
                    </form>
                    <Link to="/sign-in" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', color: '#FF69B4', marginTop: '1rem', fontSize: '16px', fontWeight: '600' }}>
                        {t('back_to_login', 'Back to Login')}
                    </Link>
                </Paper>
            </Container>
        </Box>
    );
};

export default ForgotPassword;
